// Shared filter logic + persisted-state keys for the Receive-pallets register
// (/deliveries/receive). Jason asked for the Deliveries board's filters on
// this register (2026-09-09), so the shape follows deliveriesFilter.ts: a
// field-scoped search, a chip row, facet dropdowns, an inclusive SAST date
// window. (The 90-day server window is src/lib/historyWindow.ts.) Pure given
// its inputs, so the sibling test pins every rule.

import { groupReceiptPallets } from '@/lib/receiptPalletGroups'

export type ReceiptSearchField =
  | 'any' | 'receipt_no' | 'owner' | 'sender' | 'transporter' | 'vehicle_reg' | 'doc_ref'
  | 'pallet'

// Fields swept when the operator picks "Any field". Listed explicitly so a
// future column on the register row doesn't silently start matching searches.
export const ANY_FIELD_KEYS: Exclude<ReceiptSearchField, 'any'>[] = [
  'receipt_no', 'owner', 'sender', 'transporter', 'vehicle_reg', 'doc_ref', 'pallet',
]

// Search-field labels for the dropdown. The manual quotes these.
export const RECEIPT_SEARCH_FIELDS: { value: ReceiptSearchField; label: string }[] = [
  { value: 'any',         label: 'Any field' },
  { value: 'receipt_no',  label: 'Receipt no' },
  { value: 'pallet',      label: 'Pallet no' },
  { value: 'owner',       label: 'Owner' },
  { value: 'sender',      label: 'Sender' },
  { value: 'transporter', label: 'Transporter' },
  { value: 'vehicle_reg', label: 'Vehicle reg' },
  { value: 'doc_ref',     label: 'Doc / packing list no' },
]

// The chip row. A receipt has no status of its own — its ownership case is
// what the operator sorts by — so this is the register's analogue of the
// board's status chips. Labels match the receipt card's ownership badge.
export const OWNERSHIP_CHIPS: { value: string; label: string }[] = [
  { value: 'ALL',         label: 'All' },
  { value: 'OWN',         label: 'Own fruit' },
  { value: 'THIRD_PARTY', label: '3rd-party storage' },
]

// LocalStorage keys for the persisted filters (base keys — the tenant-scoped
// ones get the tenant id suffixed by useTenantPersistedState).
export const RECEIPT_FILTER_STORAGE_KEYS = {
  ownershipFilter: 'kf49.palletReceipts.ownershipFilter',
  ownerFilter:     'kf49.palletReceipts.ownerFilter',
  varietyFilter:   'kf49.palletReceipts.varietyFilter',
  senderFilter:    'kf49.palletReceipts.senderFilter',
  fromFilter:      'kf49.palletReceipts.fromFilter',
  toFilter:        'kf49.palletReceipts.toFilter',
  searchField:     'kf49.palletReceipts.searchField',
  searchQuery:     'kf49.palletReceipts.searchQuery',
  showAllHistory:  'kf49.palletReceipts.showAllHistory',
  waitingOnly:     'kf49.palletReceipts.waitingOnly',
} as const

/** F07 UF-17: where a link opens the register on its Waiting view (the Runs card).
 *  The register reads `view=waiting` once and turns the Waiting chip on. */
export const RECEIPTS_WAITING_PATH = '/deliveries/receive?view=waiting'
export const isWaitingViewRequest = (params: URLSearchParams): boolean => params.get('view') === 'waiting'

export interface ReceiptPalletFacts {
  // The three numbers a pallet on this receipt can be searched by — see
  // palletRefs. pallet_id is ours and always present; the other two are the
  // sender's and are usually not.
  pallet_id:    string
  external_ref: string | null
  sscc:         string | null
  variety_code: string | null
  fruit_tokens: { variety: string } | null
  // UF-15: read for the card's counts. Optional so a row without them counts as unknown.
  status?:       string
}

/**
 * The register row's filterable shape. The `.select()` in ReceivePallets.tsx
 * is the contract: `runs → pallets` is the receipt's synthetic run (the same
 * path ReceiptDetail reads the receipt's pallets by), embedded for the
 * Variety facet.
 */
export interface ReceiptFilterRow {
  receipt_no:  string
  ownership:   'OWN' | 'THIRD_PARTY'
  sender:      string
  transporter: string | null
  vehicle_reg: string | null
  doc_ref:     string | null
  received_at: string
  pallet_count?: number
  growers:     { name: string } | null
  runs:        { pallets: ReceiptPalletFacts[] } | null
}

export interface PalletReceiptsFilter {
  /** 'ALL' | 'OWN' | 'THIRD_PARTY' ('' reads as ALL — a cleared persisted value) */
  ownershipFilter: string
  ownerFilter:     string
  varietyFilter:   string
  senderFilter:    string
  fromFilter:      string   // YYYY-MM-DD
  toFilter:        string   // YYYY-MM-DD
  searchField:     ReceiptSearchField
  searchQuery:     string
  /** UF-15 Waiting chip: only receipts that still hold pallets to place */
  waitingOnly?:    boolean
}

export interface ReceiptCounts { toPlace: number; inCold: number; gone: number }

/**
 * UF-15: what is left on a receipt, from the pallets the register already
 * reads. To place / in cold come from the live pallets. Gone is worked out:
 * a retipped or run-taken pallet is soft-deleted and the read never sees it,
 * so gone = the receipt's pallet count minus its live pallets, plus live ones
 * no longer in stock (dispatched). Null when the read cannot say (no pallets
 * embed, no status, no count): the card then shows a dash, never a false 0.
 */
export function receiptCounts(r: ReceiptFilterRow): ReceiptCounts | null {
  const pallets = r.runs?.pallets
  if (!pallets || typeof r.pallet_count !== 'number') return null
  if (pallets.some(p => typeof p.status !== 'string')) return null
  const g = groupReceiptPallets(pallets.map(p => ({ pallet_id: p.pallet_id, status: p.status as string, deleted_at: null })))
  return {
    toPlace: g.toPlace.length,
    inCold: g.inCold.length,
    gone: Math.max(0, r.pallet_count - pallets.length) + g.gone.length,
  }
}

const receiptOwnerName = (r: ReceiptFilterRow): string => (r.growers?.name ?? '').trim()

/**
 * Distinct varieties across the receipt's pallets. The NAME the operator typed
 * on the line (fruit_tokens.variety — required) wins; the optional code is the
 * fallback — the same pair ReceiptDetail's pallet editor prefills from. A run
 * the read can't see yields none, never a guess.
 */
export function receiptVarieties(r: ReceiptFilterRow): string[] {
  const set = new Set<string>()
  for (const p of r.runs?.pallets ?? []) {
    const v = (p.fruit_tokens?.variety || p.variety_code || '').trim()
    if (v) set.add(v)
  }
  return [...set]
}

/**
 * Every number ONE pallet can be found by, in the order an operator is likely
 * to be holding them:
 *
 *   pallet_id     ours, minted on receipt and written onto the pallet
 *   external_ref  the SENDER's pallet number, verbatim off their label
 *   sscc          the SSCC on their GS1 label, when the label carries one
 *
 * All three, because which one survives the trip is not our choice: a
 * third-party pallet standing in a cell often shows only the sender's sticker,
 * and the query that follows it ("whose is this, and what did it come in on?")
 * is the reason this register is searchable at all.
 */
function palletRefs(p: ReceiptPalletFacts): string[] {
  return [p.pallet_id, p.external_ref, p.sscc].map(v => (v ?? '').trim()).filter(Boolean)
}

/**
 * The pallet numbers on this receipt, as ONE searchable haystack.
 *
 * Newline-joined rather than space-joined so a term can never match by
 * straddling two pallets — the string is only ever fed to `.includes()`, and a
 * search box cannot produce a newline.
 */
function receiptPalletHaystack(r: ReceiptFilterRow): string {
  return (r.runs?.pallets ?? []).flatMap(palletRefs).join('\n')
}

/**
 * Which pallets on this receipt the search actually matched — what the register
 * puts on the card so a receipt pulled out of two hundred says WHY it matched.
 *
 * Empty unless the search is looking at pallet numbers at all (the 'pallet'
 * field, or the 'any' sweep that includes it), so a search for a sender name
 * never decorates cards with pallet ids it did not match on.
 */
export function matchedPalletIds(r: ReceiptFilterRow, f: PalletReceiptsFilter): string[] {
  if (f.searchField !== 'pallet' && f.searchField !== 'any') return []
  const term = f.searchQuery.trim().toLowerCase()
  if (!term) return []
  return (r.runs?.pallets ?? [])
    .filter(p => palletRefs(p).some(v => v.toLowerCase().includes(term)))
    .map(p => p.pallet_id)
}

const byName = (a: string, b: string) => a.localeCompare(b)

/**
 * The facet dropdown feeds. Owners union the master owner list with the names
 * on existing receipts — the same rule as the board's grower facet — so an
 * owner created a minute ago is pickable before their first receipt lands.
 */
export function receiptFacetOptions(rows: ReceiptFilterRow[], masterOwners: string[]) {
  const owners = new Set<string>()
  const varieties = new Set<string>()
  const senders = new Set<string>()
  for (const r of rows) {
    const o = receiptOwnerName(r); if (o) owners.add(o)
    const s = r.sender.trim();     if (s) senders.add(s)
    for (const v of receiptVarieties(r)) varieties.add(v)
  }
  for (const o of masterOwners) { const t = o.trim(); if (t) owners.add(t) }
  return {
    owners:    [...owners].sort(byName),
    varieties: [...varieties].sort(byName),
    senders:   [...senders].sort(byName),
  }
}

function searchValue(r: ReceiptFilterRow, k: Exclude<ReceiptSearchField, 'any'>): string {
  if (k === 'owner')  return receiptOwnerName(r)
  // The one field that does not live on the receipt row: it is the pallets the
  // receipt brought in, read off the embed the Variety facet already needs.
  if (k === 'pallet') return receiptPalletHaystack(r)
  return r[k] ?? ''
}

/**
 * Free-text search across one chosen field or the ANY_FIELD_KEYS sweep.
 * `term` must already be trimmed + lower-cased (hoisted out of the row loop).
 */
function matchesSearch(r: ReceiptFilterRow, field: ReceiptSearchField, term: string): boolean {
  if (!term) return true
  const keys = field === 'any' ? ANY_FIELD_KEYS : [field]
  return keys.some(k => searchValue(r, k).toLowerCase().includes(term))
}

// One rule per facet; an unset facet ('' — or 'ALL' for the chip) matches all.
const matchesOwnership = (r: ReceiptFilterRow, f: string) => !f || f === 'ALL' || r.ownership === f
const matchesOwner     = (r: ReceiptFilterRow, f: string) => !f || receiptOwnerName(r) === f
const matchesSender    = (r: ReceiptFilterRow, f: string) => !f || r.sender.trim() === f
const matchesVariety   = (r: ReceiptFilterRow, f: string) => !f || receiptVarieties(r).includes(f)

function matchesFacets(r: ReceiptFilterRow, f: PalletReceiptsFilter): boolean {
  return matchesOwnership(r, f.ownershipFilter)
    && matchesOwner(r, f.ownerFilter)
    && matchesSender(r, f.senderFilter)
    && matchesVariety(r, f.varietyFilter)
    && matchesWaiting(r, f)
}

// The Waiting chip hides receipts with nothing left to place, and it is
// persisted, so it stays on between visits. A pallet-number search that
// matches a pallet on the receipt still shows it: the operator is looking for
// THAT pallet, which is often one already retipped (Rika's 94464, 20 Oct 2026).
function matchesWaiting(r: ReceiptFilterRow, f: PalletReceiptsFilter): boolean {
  if (!f.waitingOnly || (receiptCounts(r)?.toPlace ?? 1) > 0) return true
  return matchedPalletIds(r, f).length > 0
}

// Inclusive date window: `to` compares against the start of the NEXT day so a
// receipt logged at 23:50 still falls under that calendar day. SAST-anchored
// (UTC+2) — the same rule as filterDeliveries.
function matchesWindow(ts: number, fromTs: number | null, toTs: number | null): boolean {
  if (fromTs !== null && ts < fromTs) return false
  if (toTs   !== null && ts >= toTs)  return false
  return true
}

export function filterPalletReceipts<T extends ReceiptFilterRow>(rows: T[], f: PalletReceiptsFilter): T[] {
  const fromTs = f.fromFilter ? new Date(`${f.fromFilter}T00:00:00+02:00`).getTime() : null
  const toTs   = f.toFilter   ? new Date(`${f.toFilter}T00:00:00+02:00`).getTime() + 86_400_000 : null
  const term = f.searchQuery.trim().toLowerCase()
  return rows.filter(r =>
    matchesFacets(r, f)
    && matchesWindow(new Date(r.received_at).getTime(), fromTs, toTs)
    && matchesSearch(r, f.searchField, term),
  )
}
